import { StreamingPlayerWebClientConfigV2 } from '@heardis/stream-contracts';
import { createContext, useContext, useReducer } from 'react';
import { Logger } from '../utils/logger';

const logger = new Logger('ConfigContext');

export type ConfigContextProps = StreamingPlayerWebClientConfigV2;

export type ConfigContextProviderProps = {
  children: React.ReactNode;
};

export enum ConfigContextAction {
  SET_CONFIG = 'SET_CONFIG',
}

type Action =
  | { type: ConfigContextAction.SET_CONFIG; config: ConfigContextProps };

const emptyContext = {} as StreamingPlayerWebClientConfigV2;

/** @TODO shouldn't be a default compilationConfig, especially when it's getting merged */
const ConfigContext = createContext<any>(emptyContext);

export const configReducer = (state: ConfigContextProps, action: Action): ConfigContextProps => {
  switch (action.type) {
    case ConfigContextAction.SET_CONFIG:
      return action.config;

    default:
      logger.warn('Unhandled action type:', action.type);
      return state;
  }
};

function ConfigContextProvider({ children }: ConfigContextProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(configReducer, emptyContext);

  const setConfig = (config: StreamingPlayerWebClientConfigV2) => {
    dispatch({ type: ConfigContextAction.SET_CONFIG, config });
  };

  return (
    <ConfigContext.Provider value={{ ...state, dispatch, setConfig }}>
      {children}
    </ConfigContext.Provider>
  );
}

type UseConfigContextProps = {
  setConfig: (config: StreamingPlayerWebClientConfigV2) => void;
} & ConfigContextProps;

function useConfigContext(): UseConfigContextProps {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  return context;
}

export { ConfigContextProvider, useConfigContext };
